/**
 * Wallet adapter contract.
 *
 * Every wallet backend (web3-react default, Privy, Para, DogeOS) implements this
 * interface. An adapter has two parts:
 *   1. `Provider` — a React component that mounts the SDK's context. It wraps the
 *      whole app (see `WalletProviderGate`), so the SDK's own hooks are available
 *      to `useController`. For the default web3-react kind this is a passthrough.
 *   2. `useController` — a hook (run inside `Provider`) that exposes an imperative
 *      controller: open the SDK's native connect modal, disconnect, switch chain,
 *      and read the live EIP-1193 provider + account.
 *
 * The controller's `connect()` returns a raw EIP-1193 provider. `WalletBridge`
 * feeds that provider into the existing `EIP1193Connector` so `@web3-react/core`
 * (and therefore `useWallet` and every downstream consumer) sees a normal active
 * connection — no downstream code changes.
 */
import type { ComponentType, ReactNode } from "react";
import type { ChainIdEnum } from "../../../enums/chain";
import type { WalletProviderKind } from "../../../enums/walletProvider";

/** Result of a successful connect: a live EIP-1193 provider plus its account/chain. */
export interface ConnectResult {
  /** EIP-1193 provider (has `request`; may also expose `on`/`removeListener`). */
  provider: EIP1193Like;
  account: string;
  chainId: number;
}

/** Minimal EIP-1193 surface the bridge relies on. */
export interface EIP1193Like {
  request: (args: { method: string; params?: unknown[] | object }) => Promise<any>;
  on?: (event: string, listener: (...args: any[]) => void) => void;
  removeListener?: (event: string, listener: (...args: any[]) => void) => void;
  [key: string]: any;
}

/** SDK-originated events the bridge mirrors into web3-react. */
export interface WalletAdapterEvents {
  onAccountsChanged?: (accounts: string[]) => void;
  onChainChanged?: (chainId: number) => void;
  onDisconnect?: () => void;
}

/**
 * Thrown by `connect()` when the user closes/rejects the SDK modal. Lets the UI
 * distinguish a deliberate cancel (no error toast, just reset) from a real failure.
 */
export class WalletConnectCancelledError extends Error {
  constructor(message = "Wallet connection cancelled") {
    super(message);
    this.name = "WalletConnectCancelledError";
  }
}

/**
 * A newer `connect()` replaced this one before it settled.
 *
 * Extends the cancel type on purpose: for every UI decision this IS a cancel —
 * the second call is now driving the flow, so the first must exit quietly. What
 * it must NOT do is vanish. Leaving it unsettled is precisely the latch bug this
 * module exists to prevent: the first caller's `finally` never runs, so
 * `connectWalletInFlightRef` stays true and the button goes inert.
 */
export class WalletConnectSupersededError extends WalletConnectCancelledError {
  constructor(message = "Wallet connection cancelled — superseded by a newer connect") {
    super(message);
    this.name = "WalletConnectSupersededError";
  }
}

/**
 * The SDK never answered within the bounded window.
 *
 * Deliberately NOT a subclass of the cancel type, because the two need opposite
 * treatment. A cancel is the expected exit — silent, no toast, no telemetry. A
 * timeout is a fault the user did not cause: tell them, and report it. Folding
 * them together would bury the timeouts under the vastly more numerous cancels
 * and make the signal useless, which is how this class of bug stayed invisible.
 */
export class WalletConnectTimeoutError extends Error {
  constructor(message = "Wallet connection timed out") {
    super(message);
    this.name = "WalletConnectTimeoutError";
  }
}

/**
 * Did this connect end without a usable wallet, for a reason the UI should treat
 * as "stop quietly and re-offer the picker"?
 *
 * The `name` comparison is not redundant with `instanceof`. Adapters are
 * lazy-loaded chunks (see `registry.ts`), and `instanceof` across a chunk or HMR
 * boundary is a classic silent failure — the error is the right shape but a
 * different class identity.
 */
export const isWalletConnectAborted = (error: unknown): boolean =>
  error instanceof WalletConnectCancelledError ||
  error instanceof WalletConnectTimeoutError ||
  (error as Error | undefined)?.name === "WalletConnectCancelledError" ||
  (error as Error | undefined)?.name === "WalletConnectSupersededError" ||
  (error as Error | undefined)?.name === "WalletConnectTimeoutError";

/** A newer `connect()` replaced this one — do not tear down the whole flow. */
export const isWalletConnectSuperseded = (error: unknown): boolean =>
  error instanceof WalletConnectSupersededError ||
  (error as Error | undefined)?.name === "WalletConnectSupersededError";

export interface WalletAdapterController {
  kind: WalletProviderKind;
  /** True once the SDK provider has mounted and finished initializing. */
  isReady: boolean;
  /**
   * Open the SDK's native connect modal and resolve once a provider + account are
   * available. Not used for the WEB3REACT kind (that path stays inside
   * web3-react).
   *
   * IMPLEMENTATIONS MUST ALWAYS SETTLE. This is a hard requirement, not advice.
   * There is exactly one caller (`useWallet.activate`), and it sets an in-flight
   * latch that it clears only in a `finally`. A promise that never settles
   * therefore leaves that latch set forever: every later click is swallowed by
   * an early return, the app's own modal stays hidden behind `hideBehindSdkModal`,
   * nothing throws, nothing reaches Sentry, and a reload does not help if the
   * SDK's session persists. The Connect Wallet button simply dies.
   *
   * That is not hypothetical — it has shipped twice. Para (fixed in 0b598f9)
   * left `reject` stored and called from nowhere on modal dismissal. Privy
   * called `login()` while already logged in, which Privy answers with a
   * *warning* and a no-op, so its `onError` never fired.
   *
   * Concretely, an implementation must:
   *   - reject with `WalletConnectCancelledError` when the user dismisses;
   *   - reject with `WalletConnectTimeoutError` under a bounded hard cap, so an
   *     SDK that simply never answers cannot park the promise;
   *   - reject a superseded connect with `WalletConnectSupersededError` rather
   *     than orphaning it;
   *   - settle (reject) on unmount.
   *
   * `usePendingConnect` implements all four. Use it rather than hand-rolling a
   * pending ref — three adapters hand-rolled it and two got it wrong.
   */
  connect: () => Promise<ConnectResult>;
  /** Fully disconnect / log out of the SDK (clears the SDK's own session). */
  disconnect: () => Promise<void>;
  /** Switch the connected wallet to `chainId` (add-chain fallback handled inside). */
  switchChain?: (chainId: number) => Promise<void>;
  /** The live EIP-1193 provider if currently connected, else undefined. */
  getProvider: () => EIP1193Like | undefined;
  /** The current account if connected, else undefined. */
  getAccount: () => string | undefined;
  /** True when the SDK already has a restorable session (drives eager reconnect). */
  hasSession: () => boolean;
  /** Subscribe to SDK account/chain/disconnect events. Returns an unsubscribe fn. */
  subscribe?: (events: WalletAdapterEvents) => () => void;
}

export interface WalletAdapter {
  kind: WalletProviderKind;
  /** React provider that must wrap the app for this SDK (Fragment for web3react). */
  Provider: ComponentType<{ chainId: ChainIdEnum; children: ReactNode }>;
  /** Hook, run inside `Provider`, returning the imperative controller. */
  useController: () => WalletAdapterController;
}
